<template>
  <div class="mt-6">
    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-lg font-semibold font-heading text-gray-900 dark:text-gray-100">
          Density Audit
        </h3>
        <button
          v-if="canRunAudit"
          @click="startAudit"
          :disabled="loading"
          class="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 disabled:opacity-50">
          {{ audit ? 'Re-run Audit' : 'Run Density Audit' }}
        </button>
      </div>

      <!-- Progress -->
      <div v-if="running" class="mb-4">
        <div class="flex items-center gap-2 text-sm text-primary-500">
          <svg class="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none" /><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
          <span>{{ progressMessage }}</span>
        </div>
      </div>

      <!-- Clean State -->
      <div v-if="audit?.all_clean" class="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
        <div class="flex items-center gap-2">
          <svg class="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" /></svg>
          <span class="text-green-700 dark:text-green-300 font-medium">All keywords within ±1 tolerance!</span>
        </div>
        <p class="text-sm text-green-600 dark:text-green-400 mt-1">
          Cycle {{ audit.cycle_number }} — {{ audit.created_at ? 'Completed' : '' }}
        </p>
      </div>

      <!-- Discrepancy Tables -->
      <div v-if="audit && !audit.all_clean && !running">
        <div class="mb-2 text-sm text-gray-500">
          Cycle {{ audit.cycle_number }} — {{ audit.discrepancy_count }} discrepancies found
        </div>

        <!-- Word Count Check -->
        <div v-if="audit.word_count_check?.actual" class="mb-4 p-3 rounded-lg" :class="audit.word_count_check.within_tolerance ? 'bg-green-50 dark:bg-green-900/20' : 'bg-yellow-50 dark:bg-yellow-900/20'">
          <span class="text-sm font-medium">Word Count:</span>
          <span class="text-sm ml-1">{{ audit.word_count_check.actual }} words (target: {{ audit.word_count_check.target }}, {{ audit.word_count_check.delta_pct > 0 ? '+' : '' }}{{ audit.word_count_check.delta_pct }}%)</span>
        </div>

        <!-- Body Discrepancies -->
        <div v-if="audit.body_discrepancies?.length" class="mb-4">
          <h4 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Body Count Discrepancies
          </h4>
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="border-b border-gray-200 bg-gray-50 dark:bg-gray-900/50">
                  <th class="text-left py-2 px-2 text-gray-500 font-medium">
                    Keyword
                  </th>
                  <th class="text-left py-2 px-2 text-gray-500 font-medium">
                    Tier
                  </th>
                  <th class="text-center py-2 px-2 text-gray-500 font-medium">
                    Target
                  </th>
                  <th class="text-center py-2 px-2 text-gray-500 font-medium">
                    Actual
                  </th>
                  <th class="text-center py-2 px-2 text-gray-500 font-medium">
                    Delta
                  </th>
                  <th class="text-center py-2 px-2 text-gray-500 font-medium">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="d in audit.body_discrepancies" :key="d.keyword" class="border-b border-gray-100">
                  <td class="py-2 px-2 font-medium">
                    {{ d.keyword }}
                  </td>
                  <td class="py-2 px-2">
                    <span class="px-2 py-0.5 rounded-full text-xs" :class="tierClass(d.tier)">{{ d.tier }}</span>
                  </td>
                  <td class="py-2 px-2 text-center">
                    {{ d.target }}
                  </td>
                  <td class="py-2 px-2 text-center">
                    {{ d.actual }}
                  </td>
                  <td class="py-2 px-2 text-center font-mono" :class="d.delta > 0 ? 'text-red-600' : 'text-primary-600'">
                    {{ d.delta > 0 ? '+' : '' }}{{ d.delta }}
                  </td>
                  <td class="py-2 px-2 text-center">
                    <span class="px-2 py-0.5 rounded-full text-xs" :class="statusClass(d.status)">{{ d.status }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Heading Discrepancies -->
        <div v-if="audit.heading_discrepancies?.length" class="mb-4">
          <h4 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Heading Discrepancies
          </h4>
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="border-b border-gray-200 bg-gray-50 dark:bg-gray-900/50">
                  <th class="text-left py-2 px-2 text-gray-500 font-medium">
                    Keyword
                  </th>
                  <th class="text-center py-2 px-2 text-gray-500 font-medium">
                    Target
                  </th>
                  <th class="text-center py-2 px-2 text-gray-500 font-medium">
                    Actual
                  </th>
                  <th class="text-center py-2 px-2 text-gray-500 font-medium">
                    H1
                  </th>
                  <th class="text-center py-2 px-2 text-gray-500 font-medium">
                    H2
                  </th>
                  <th class="text-center py-2 px-2 text-gray-500 font-medium">
                    H3
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="d in audit.heading_discrepancies" :key="d.keyword" class="border-b border-gray-100">
                  <td class="py-2 px-2 font-medium">
                    {{ d.keyword }}
                  </td>
                  <td class="py-2 px-2 text-center">
                    {{ d.target }}
                  </td>
                  <td class="py-2 px-2 text-center">
                    {{ d.actual }}
                  </td>
                  <td class="py-2 px-2 text-center">
                    {{ d.h1 }}
                  </td>
                  <td class="py-2 px-2 text-center">
                    {{ d.h2 }}
                  </td>
                  <td class="py-2 px-2 text-center">
                    {{ d.h3 }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Placement Discrepancies -->
        <div v-if="audit.placement_discrepancies?.length" class="mb-4">
          <h4 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Placement Issues
          </h4>
          <div v-for="d in audit.placement_discrepancies" :key="d.keyword + d.position" class="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 mb-2">
            <span class="text-sm text-yellow-800">
              "{{ d.keyword }}" missing from <strong>{{ d.position?.replace(/_/g, ' ') }}</strong>
            </span>
          </div>
        </div>

        <!-- Cycle history summary -->
        <div v-if="audit.cycle_number > 1" class="mt-2 text-xs text-gray-400">
          Completed {{ audit.cycle_number }} audit cycles with progressive tolerance.
        </div>
      </div>

      <!-- No audit yet -->
      <div v-if="!audit && !running" class="text-sm text-gray-500">
        Run a density audit to verify keyword counts against targets.
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import consumer from '~/channels/consumer'
import { runDensityAudit, getDensityAudit } from '~/javascript/api/density_audit'

const props = defineProps({
  pageId: { type: String, required: true }
})

const emit = defineEmits(['audit-clean'])

const audit = ref(null)
const optimizationStatus = ref(null)
const loading = ref(false)
const running = ref(false)
const progressMessage = ref('')
let subscription = null
let pollTimer = null

const canRunAudit = computed(() => {
  if (running.value || loading.value) return false
  const status = optimizationStatus.value
  return status && ['completed', 'audit_clean', 'auditing'].includes(status)
})

function tierClass(tier) {
  const classes = {
    primary: 'bg-primary-100 text-primary-700',
    secondary: 'bg-purple-100 text-purple-700',
    supporting: 'bg-gray-100 text-gray-600',
    lsi: 'bg-teal-100 text-teal-700'
  }
  return classes[tier] || 'bg-gray-100 text-gray-600'
}

function statusClass(status) {
  return {
    over: 'bg-red-100 text-red-700',
    under: 'bg-primary-100 text-primary-700',
    acceptable: 'bg-green-100 text-green-700'
  }[status] || 'bg-gray-100 text-gray-600'
}

async function fetchAudit() {
  try {
    const { data } = await getDensityAudit(props.pageId)
    audit.value = data.audit
    optimizationStatus.value = data.optimization_status
    if (data.audit?.status === 'counting' || optimizationStatus.value === 'auditing') {
      running.value = true
      progressMessage.value = 'Density audit in progress...'
      startPolling()
    }
  } catch (e) {
    console.error('Failed to fetch audit:', e)
  }
}

async function startAudit() {
  loading.value = true
  running.value = true
  progressMessage.value = 'Starting density audit...'
  try {
    await runDensityAudit(props.pageId)
    startPolling()
  } catch (e) {
    running.value = false
    console.error('Failed to start audit:', e)
  } finally {
    loading.value = false
  }
}

function startPolling() {
  stopPolling()
  pollTimer = setInterval(async () => {
    try {
      const { data } = await getDensityAudit(props.pageId)
      const s = data.audit?.status
      if (s && ['clean', 'completed', 'failed'].includes(s)) {
        audit.value = data.audit
        optimizationStatus.value = data.optimization_status
        running.value = false
        if (data.audit.all_clean) emit('audit-clean')
        stopPolling()
      }
    } catch (_e) { /* ignore polling errors */ }
  }, 5000)
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

function setupCable() {
  subscription = consumer.subscriptions.create(
    { channel: 'DensityAuditChannel', page_id: props.pageId },
    {
      received(data) {
        progressMessage.value = data.message || ''
        if (data.step === 'clean') {
          running.value = false
          stopPolling()
          fetchAudit()
          emit('audit-clean')
        } else if (data.step === 'error') {
          running.value = false
          stopPolling()
          fetchAudit()
        } else {
          running.value = true
        }
      }
    }
  )
}

onMounted(() => {
  fetchAudit()
  setupCable()
})

onUnmounted(() => {
  subscription?.unsubscribe()
  stopPolling()
})
</script>
